import {SC_LocationServiceRecurrence} from "./SC_LocationServiceRecurrence";
import {SC_YesNoEnum} from "./enums/SC_YesNoEnum";
import {SC_GenericConfirmation} from "./SC_GenericConfirmation";

export class SC_LocationService {
    /**
     * Input property name constants for fromAPI
     */
    static inputKeys = {
        internalID: 'internal_id',
        locationID: 'location_id',
        serviceName: 'service_name',
        serviceSearchString: 'search_string',
        shortName: 'short_name',
        recurrences: 'recurrences',
        mon: 'mon',
        confirmations: 'confirmations',
    } as const;

    internalID: number | null;
    locationID: number | null;
    serviceName: string | null;
    serviceSearchString: string | null;
    shortName: string | null;
    recurrences: SC_LocationServiceRecurrence[] | null;
    mon: number | null;
    confirmations: SC_GenericConfirmation[] | null = null;

    constructor({
        internalID = null,
        locationID = null,
        serviceName = null,
        serviceSearchString = null,
        shortName = null,
        recurrences = null,
        mon = null,
        confirmations = null,
    } = {}) {
        this.internalID = internalID;
        this.locationID = locationID;
        this.serviceName = serviceName;
        this.serviceSearchString = serviceSearchString;
        this.shortName = shortName;
        this.recurrences = recurrences;
        this.mon = mon;
        this.confirmations = confirmations;
    }

    static fromAPI(jsonin: Record<string, any>): SC_LocationService {
        return new SC_LocationService({
            internalID: jsonin[SC_LocationService.inputKeys.internalID] ?? null,
            locationID: jsonin[SC_LocationService.inputKeys.locationID] ?? null,
            serviceName: jsonin[SC_LocationService.inputKeys.serviceName] ?? null,
            serviceSearchString: jsonin[SC_LocationService.inputKeys.serviceSearchString] ?? null,
            shortName: jsonin[SC_LocationService.inputKeys.shortName] ?? null,
            recurrences: jsonin[SC_LocationService.inputKeys.recurrences]?.map((o: any) => SC_LocationServiceRecurrence.fromAPI(o)) ?? null,
            mon: jsonin[SC_LocationService.inputKeys.mon] ?? null,
            confirmations: jsonin[SC_LocationService.inputKeys.confirmations]?.map((c: Record<string, any>) => SC_GenericConfirmation.fromAPI(c)) ?? null,
        });
    }

    toAPI(): Record<string, unknown> {
        return {
            [SC_LocationService.inputKeys.internalID]: this.internalID,
            [SC_LocationService.inputKeys.locationID]: this.locationID,
            [SC_LocationService.inputKeys.serviceName]: this.serviceName,
            [SC_LocationService.inputKeys.serviceSearchString]: this.serviceSearchString,
            [SC_LocationService.inputKeys.shortName]: this.shortName,
            [SC_LocationService.inputKeys.recurrences]: this.recurrences?.map((it: SC_LocationServiceRecurrence) => it.toAPI()),
            [SC_LocationService.inputKeys.mon]: this.mon,
            [SC_LocationService.inputKeys.confirmations]: this.confirmations?.map(it => it.toAPI()) ?? null,
        };
    }

    isDegenerate(): string | null {
        if (this.serviceName == null)
            return "Service Name is required";
        return null;
    }

    clone(): SC_LocationService {
        return new SC_LocationService({
            internalID: this.internalID,
            locationID: this.locationID,
            serviceName: this.serviceName,
            serviceSearchString: this.serviceSearchString,
            shortName: this.shortName,
            recurrences: this.recurrences?.map((r: SC_LocationServiceRecurrence) => r.clone()) ?? null,
            mon: this.mon,
            confirmations: this.confirmations?.map(it => it.clone()) ?? null,
        });
    }

    /**
     * Merges another SC_LocationService object into this one, replacing null fields
     */
    merge(other: SC_LocationService): SC_LocationService {
        if (other.internalID !== null) this.internalID = other.internalID;
        if (other.locationID !== null) this.locationID = other.locationID;
        if (other.serviceName !== null) this.serviceName = other.serviceName;
        if (other.serviceSearchString !== null) this.serviceSearchString = other.serviceSearchString;
        if (other.shortName !== null) this.shortName = other.shortName;
        if (other.mon !== null) this.mon = other.mon;
        if (other.confirmations !== null) this.confirmations = other.confirmations;

        // Handle recurrences array
        if (other.recurrences !== null) {
            this.recurrences = other.recurrences.map((it: SC_LocationServiceRecurrence) => it.clone());
        }

        return this;
    }

    static async getServices(locids: string[], doRequest: (formData: FormData, action: string, doLoading: boolean) => Promise<any>): Promise<{[key: string]: SC_LocationService[]}> {
        const formData = new FormData();

        formData.append("locids", locids.join(","));

        const back = await doRequest(formData, "locations_services", true);

        const out = Object.entries(back.reason).reduce((out: {[key: string]: SC_LocationService[]}, [key, value]: [string, any]) => {
            out[key] = (value as any[]).map((it: any) => SC_LocationService.fromAPI(it));

            return out;
        }, {});

        console.log("resolving", out);

        return out;
    }

    get avg_mon(): number {
        const loadFactor = this.recurrences.reduce((out: number, cur: SC_LocationServiceRecurrence) => {
            return out + (cur.days?.size ?? 7) / 7;
        }, 0);

        return this.mon * (loadFactor / 30.5);
    }

    get total_mon(): number {
        return this.mon ?? 0;
    }

    get avg_techs(): number {
        if (!this.recurrences || this.recurrences.length === 0) return 0;
        return this.recurrences.reduce((out: number, cur: SC_LocationServiceRecurrence) => {
            return out + (cur.techs ?? 0);
        }, 0) / this.recurrences.length;
    }

    get avg_time(): number {
        if (!this.recurrences || this.recurrences.length === 0) return 0;
        return this.recurrences.reduce((out: number, cur: SC_LocationServiceRecurrence) => {
            return out + (cur.dur ?? 0) * (cur.techs ?? 0);
        }, 0) / this.recurrences.length;
    }

    get total_time(): number {
        if (!this.recurrences || this.recurrences.length === 0) return 0;
        return this.recurrences.reduce((out: number, cur: SC_LocationServiceRecurrence) => {
            return out + (cur.dur ?? 0) * (cur.techs ?? 0);
        }, 0) * 30.5;
    }
}
